import { useRef, useEffect, useState } from "react";
import { reviews } from "@/mocks/reviews";

const highlights = [
  { icon: "ri-award-line", label: "受賞歴", value: "国内ネイルコンペ3冠" },
  { icon: "ri-group-line", label: "リピート率", value: "92%" },
  { icon: "ri-map-pin-line", label: "所在地", value: "渋谷区神宮前" },
  { icon: "ri-time-line", label: "営業時間", value: "10:00 — 20:00" },
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const prevReview = () => setReviewIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  const nextReview = () => setReviewIndex((prev) => (prev + 1) % reviews.length);

  const currentReview = reviews[reviewIndex];

  return (
    <section id="about" className="py-20 lg:py-28 bg-[#FAFAF8]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* About Content */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-10 mb-24">
          {/* Left text */}
          <div
            className={`w-full lg:w-[32%] transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <p className="font-['DM_Sans'] text-xs font-medium tracking-[0.3em] uppercase text-[#C9A87C] mb-3">
              Our Story
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-5xl lg:text-6xl font-light text-[#1A1A1A] leading-tight mb-6">
              Atelier<br />NANAについて
            </h2>
            <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] leading-relaxed mb-6">
              2018年、渋谷区神宮前に誕生したAtelier NANA。「指先から始まる自信と美しさ」をコンセプトに、
              国内外のトレンドを取り入れたネイルアートをご提供しています。
            </p>
            <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] leading-relaxed mb-8">
              熟練のネイリストによる丁寧なカウンセリングと施術で、
              お客様一人ひとりのライフスタイルに合った最高の仕上がりをお届けします。
            </p>
            <button
              onClick={() => {
                const el = document.querySelector("#contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex items-center gap-2 border border-[#3D2E2A] text-[#3D2E2A] text-sm font-medium px-6 py-3 rounded-full hover:bg-[#3D2E2A] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans']"
            >
              お問い合わせ
              <i className="ri-arrow-right-up-line"></i>
            </button>
          </div>

          {/* Center — Main salon image */}
          <div
            className={`w-full lg:w-[36%] transition-all duration-700 delay-150 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="relative rounded-[24px] overflow-hidden h-[420px] lg:h-[500px] w-full">
              <img
                src="https://public.readdy.ai/ai/img_res/7807883ba8f836955d28bcaa47933460.jpg"
                alt="Atelier NANA サロン内観"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right — photos grid + highlights */}
          <div
            className={`w-full lg:flex-1 transition-all duration-700 delay-300 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1A1A1A]">
                こだわりのサロン空間
              </p>
              <i className="ri-arrow-right-up-line text-[#C9A87C]"></i>
            </div>
            {/* Top wide photo */}
            <div className="rounded-[16px] overflow-hidden h-[160px] mb-3">
              <img
                src="https://readdy.ai/api/search-image?query=beautiful%20nail%20salon%20reception%20area%2C%20minimalist%20white%20and%20nude%20beige%20interior%2C%20elegant%20counter%20with%20fresh%20flowers%2C%20warm%20ambient%20lighting%2C%20luxury%20Japanese%20beauty%20salon%20design%2C%20welcoming%20and%20serene%20atmosphere%2C%20high%20end%20interior%20photography&width=400&height=200&seq=about2&orientation=landscape"
                alt="サロン内装"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Two small photos */}
            <div className="flex gap-3 mb-6">
              <div className="flex-1 rounded-[16px] overflow-hidden h-[130px] relative">
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20nail%20polish%20bottles%20collection%20arranged%20beautifully%20on%20white%20shelf%2C%20luxury%20nail%20salon%20products%2C%20pastel%20and%20nude%20tones%20gel%20colors%2C%20minimalist%20product%20photography%2C%20elegant%20interior%20design%20detail&width=200&height=150&seq=about3&orientation=squarish"
                  alt="カラー"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                  <p className="font-['Noto_Sans_JP'] text-[10px] text-[#3D2E2A]">厳選カラー</p>
                </div>
              </div>
              <div className="flex-1 rounded-[16px] overflow-hidden h-[130px] relative">
                <img
                  src="https://readdy.ai/api/search-image?query=cozy%20nail%20salon%20manicure%20station%20close%20up%2C%20premium%20nail%20art%20tools%20neatly%20arranged%2C%20soft%20warm%20lighting%2C%20professional%20nail%20technician%20workspace%2C%20clean%20minimalist%20Japanese%20salon%20detail%2C%20high%20quality%20interior%20photography&width=200&height=150&seq=about4&orientation=squarish"
                  alt="施術スペース"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                  <p className="font-['Noto_Sans_JP'] text-[10px] text-[#3D2E2A]">こだわり空間</p>
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="grid grid-cols-2 gap-3">
              {highlights.map((h) => (
                <div key={h.label} className="bg-[#F5F0EB] rounded-[12px] p-4">
                  <div className="w-8 h-8 flex items-center justify-center mb-2">
                    <i className={`${h.icon} text-[#C9A87C] text-lg`}></i>
                  </div>
                  <p className="font-['Cormorant_Garamond'] text-base font-semibold text-[#1A1A1A]">{h.value}</p>
                  <p className="font-['Noto_Sans_JP'] text-[11px] text-[#9E9189] mt-0.5">{h.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-[#F5F0EB] rounded-[24px] p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            {/* Rating badge */}
            <div className="flex-shrink-0 text-center">
              <div className="inline-flex items-center gap-3 bg-[#3D2E2A] px-6 py-3 rounded-full mb-4">
                <i className="ri-star-fill text-[#F5C842] text-base"></i>
                <span className="font-['Cormorant_Garamond'] text-3xl font-semibold text-white">4.9</span>
              </div>
              <p className="font-['Noto_Sans_JP'] text-xs text-[#6B6B6B]">200件以上のレビュー</p>
              <p className="font-['DM_Sans'] text-xs font-semibold tracking-[0.2em] uppercase text-[#1A1A1A] mt-4">
                お客様の声
              </p>
            </div>

            {/* Review card */}
            <div className="flex-1">
              <div className="flex mb-2">
                {[...Array(currentReview.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#F5C842] text-sm"></i>
                ))}
              </div>
              <p className="font-['Noto_Sans_JP'] text-sm text-[#3A3A3A] leading-relaxed mb-6 italic">
                「{currentReview.comment}」
              </p>
              <div className="flex items-center gap-4">
                <img
                  src={currentReview.avatarUrl}
                  alt={currentReview.name}
                  className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0"
                />
                <div>
                  <p className="font-['Noto_Sans_JP'] text-sm font-medium text-[#1A1A1A]">{currentReview.name}</p>
                  <p className="font-['DM_Sans'] text-xs text-[#9E9189]">{currentReview.handle} · {currentReview.date}</p>
                </div>
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={prevReview}
                className="w-10 h-10 flex items-center justify-center border border-[#D8D2CB] rounded-lg bg-white text-[#3D2E2A] hover:bg-[#3D2E2A] hover:text-white transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <button
                onClick={nextReview}
                className="w-10 h-10 flex items-center justify-center bg-[#3D2E2A] rounded-lg text-white hover:bg-[#C9A87C] transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
